import { Candidate } from "@/types/candidate";
import { UserCircle, Circle } from "lucide-react";
import { Link } from "react-router-dom";
import { SkillBadge } from "@/components/SkillBadge";
import { EntityList } from "@/components/common/EntityList";
import { useTranslation } from "react-i18next";

interface CandidatesListProps {
  candidates: Candidate[];
  filteredCandidates: Candidate[];
  isLoading: boolean;
}

export const CandidatesList = ({
  candidates,
  filteredCandidates,
  isLoading
}: CandidatesListProps) => {
  const { t } = useTranslation();
  // Get status color based on candidate status
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "looking":
        return "text-green-500";
      case "open":
        return "text-orange-400";
      case "unavailable":
        return "text-red-500";
      default:
        return "text-gray-400";
    }
  };

  const columns = [
    {
      key: "name",
      header: t("candidates.list.name"),
      cell: (candidate: Candidate) => (
        <Link to={`/candidates/${candidate.id}`} className="hover:underline flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-gray-400" />
          {candidate.name}
        </Link>
      ),
    },
    {
      key: "function",
      header: t("candidates.list.function"),
      cell: (candidate: Candidate) => candidate.function || "-",
    },
    {
      key: "email",
      header: t("candidates.list.email"),
      cell: (candidate: Candidate) => (
        <a href={`mailto:${candidate.email}`} className="text-blue-600 hover:underline">
          {candidate.email}
        </a>
      ),
    },
    {
      key: "skills",
      header: t("candidates.list.skills"),
      cell: (candidate: Candidate) => (
        <div className="flex flex-wrap gap-1">
          {candidate.skills && candidate.skills.slice(0, 3).map(skill => (
            <SkillBadge key={skill} label={skill} />
          ))}
          {candidate.skills && candidate.skills.length > 3 && (
            <span className="text-xs text-gray-500">+{candidate.skills.length - 3} {t("candidates.list.more")}</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: t("candidates.list.status"),
      cell: (candidate: Candidate) => {
        const statusColor = getStatusColor(candidate.status);
        return (
          <div className="flex items-center gap-1.5">
            <Circle className={`h-3 w-3 fill-current ${statusColor}`} />
            <span>{candidate.status || t("candidates.list.noStatus")}</span>
          </div>
        );
      },
    },
  ];

  return (
    <EntityList
      data={filteredCandidates}
      columns={columns}
      isLoading={isLoading}
      emptyMessage={t("candidates.list.empty")}
      basePath="/candidates"
    />
  );
};
